// Build a `seed` object for the Add New Deal modal from an existing
// listing-request or buyer-request deal. Pure function — no IO, no React.
//
// The seed shape mirrors the modal's four step state slices, plus:
//   - source: "listing" | "buyer" — for UI label ("From listing" vs "From buyer request")
//   - prefilled: { [fieldName]: true } — flags so the modal can decorate
//     prefilled inputs (amber border, "From X" subtitle). Only set for
//     fields that were actually populated from the source, not defaults.
export function buildTurnInSeed(source, side) {
  if (!source) return null;

  // Prefer the side the agent explicitly picked (the "Buyer Side" / "Listing
  // Side" button) so the client type can't be thrown off by stale or wrong
  // data on the source file. Fall back to the source's own dealtype when no
  // side is supplied (e.g. legacy callers / tests).
  const isBuyer =
    side === "buyer"
      ? true
      : side === "listing"
        ? false
        : source.dealtype === "Buyer";

  const prefilled = {};
  const mark = (key, val) => {
    if (val !== undefined && val !== null && val !== "") prefilled[key] = true;
    return val;
  };

  return {
    source: isBuyer ? "buyer" : "listing",
    // For a LISTING turn-in where the agent picked an existing listing on the
    // board, carry that deal's id so the submit MOVES that card to Under
    // Contract instead of creating a duplicate. Empty for buyer side and for
    // "Start From Scratch" (those still create a new deal).
    sourceDealId: !isBuyer && source.createdat ? String(source.createdat) : "",
    // For a BUYER turn-in that came from a pipeline lead, carry the lead's id so
    // the submit can archive it (status "converted", outcome "won") — the lead
    // then leaves the active pipeline and is tracked as a fully-converted buyer.
    sourceLeadId: source.sourceLeadId || "",
    sourceLeadType: source.sourceLeadType || "",
    prefilled,
    step1: {
      mlsNumber: "",
      propertyType: mark("propertyType", source.propertyType) || "",
      earnestMoney: 0,
      offerPrice: 0,
      // Always a sale stage — that's what "turn-in" means.
      dealStage: "Under Contract",
      inspection: "",
      financingType: "",
      propertyaddress: mark("propertyaddress", source.propertyaddress) || "",
      closingdate: new Date().toISOString().slice(0, 10),
      financingDeadline: "",
      inspectionDeadline: "",
      propertyphoto: mark("propertyphoto", source.propertyphoto) || "",
      propertyphotodata: source.propertyphotodata || "",
      propertyLocation: source.propertyLocation
        ? (mark("propertyLocation", source.propertyLocation), source.propertyLocation)
        : null,
    },
    step2: {
      clientType: mark("clientType", isBuyer ? "Buyer" : "Seller"),
      clientinfo:
        Array.isArray(source.customers) && source.customers.length > 0
          ? (mark("clientinfo", source.customers), source.customers)
          : [""],
    },
    step3: {
      teamleadName: "",
      assignedteamlead: "",
      agentName: mark("agentName", source.agentName) || "",
      agentid: mark("agentid", source.agentData) || "",
      leadsource: mark("leadsource", source.leadsource) || "",
      propertyAddress: source.propertyaddress || "",
      agentCommission: 0,
      thirdpartyagentCommission: 0,
      thirdpartyagentName: "",
      fixedagentcommission: false,
      leadCommission: source.leadsource?.leadCommission || 0,
      splittype: source.splittype || "TeamSplit",
      inhousereferral: false,
      splitdescription: source.splitdescription || "Team Member Split",
      brokeradminfee: 0,
      captype: source.captype || "pre",
      captypedescription:
        source.captypedescription || "Pre-Cap (90/10 Split)",
    },
    step4: {
      flatfee: false,
      flatfeeamount: 0,
      // Carry the source's rate if it has one; otherwise default by side
      // (Buyer 2.7% / Seller 3.5%) — mirrors the manual client-type pick.
      totalcommissionrate:
        mark("totalcommissionrate", source.totalcommissionrate) ||
        (isBuyer ? 2.7 : 3.5),
      buyersidecommissionrate: "",
      sellersidecommissionrate: "",
      teamleadcommissionrate: "",
      agentCommissionamount: 0,
      teamleaddiscount: 0,
      agentdiscount: 0,
      leadsourceCommissionamount: 0,
      teamleadCommissionamount: 0,
      totalCommissionamount: 0,
      bankcommission: 0,
      thirdpartyagentcommissionamount: 0,
      takehomecommissionamount: 0,
    },
  };
}
